import React, {useEffect, useRef, useState} from "react";
import {getJSON, post, page, card, whiteBtn, inputStyle, Countdown} from "./ui";
import {ZedSays} from "./zed";
import {renderStrokes} from "../dancelestrations/strokeRenderer";

const parse = (raw) => { try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; } };

// Zed is centered in whatever room is left above the drawing and the answer, which sit against the bottom.
const Prompt = ({state}) => {
    const who = state.you.partner ? state.you.partner.name : null;
    return <div style={{flex: 1, display: 'flex', alignItems: 'center'}}>
        <ZedSays line={who ? `What did ${who} draw?` : 'What is it?'} />
    </div>;
};

const canvasStyle = {width: '100%', aspectRatio: '1 / 1', height: 'auto', backgroundColor: '#0a0a0a', border: '1.5px solid rgba(176,141,87,0.55)', borderRadius: 12};

export const GuessPhase = ({state, offset}) => {
    const [drawings, setDrawings] = useState(null);
    const [last, setLast] = useState(null); // {recording, guess} of the most recent submit

    useEffect(() => {
        let live = true;
        getJSON('/demo/drawings-to-guess').then(d => { if (live) setDrawings((d && d.drawings) || []); });
        return () => { live = false; };
    }, [state.round]);

    if (!drawings) return <div style={{...page, alignItems: 'center', justifyContent: 'center'}}>…</div>;
    const remaining = drawings.filter(d => !d.guessed);
    const markGuessed = (id, g, recording) => {
        setLast({recording, guess: g});
        setDrawings(ds => ds.map(d => d.id === id ? {...d, guessed: true, my_guess: g} : d));
    };

    // Done guessing → stay on the drawing with your guess echoed (like the show),
    // not a separate holding screen. (Survives reload via the server's my_guess.)
    if (remaining.length === 0) {
        const lastGuessed = last || (() => {
            const g = drawings.filter(d => d.guessed && d.my_guess).slice(-1)[0];
            return g ? {recording: g.recording, guess: g.my_guess} : null;
        })();
        return <Submitted last={lastGuessed} state={state} offset={offset} />;
    }

    return <div style={{...page, gap: 12}}>
        <Countdown endsAt={state.phase_ends_at} offset={offset} />
        <Prompt state={state} />
        <GuessCard key={remaining[0].id} drawing={remaining[0]} onGuessed={markGuessed} />
    </div>;
};

const GuessCard = ({drawing, onGuessed}) => {
    const canvasRef = useRef(null);
    const [guess, setGuess] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const rec = parse(drawing.recording);
        if (canvasRef.current && rec) renderStrokes(canvasRef.current, rec, {animated: false});
    }, [drawing.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!guess.trim()) return;
        setBusy(true);
        const res = await post('/demo/guess', {drawing_id: drawing.id, guess: guess.trim()});
        if (res.ok) onGuessed(drawing.id, guess.trim(), drawing.recording);
        else setBusy(false);
    };

    return <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <canvas ref={canvasRef} width={500} height={500} style={canvasStyle} />
        <input type="text" className="demo-input" style={{...inputStyle, textAlign: 'center'}} value={guess} onChange={e => setGuess(e.target.value)} placeholder="What is it?"
               onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
        <button style={whiteBtn} disabled={busy || !guess.trim()} onClick={submit}>Lock in guess</button>
    </div>;
};

// Post-submit: the show's look — drawing stays up, your guess echoed back.
const Submitted = ({last, state, offset}) => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const rec = parse(last && last.recording);
        if (canvasRef.current && rec) renderStrokes(canvasRef.current, rec, {animated: false});
    }, [last]);

    return <div style={{...page, gap: 12}}>
        <Countdown endsAt={state.phase_ends_at} offset={offset} />
        <Prompt state={state} />
        <canvas ref={canvasRef} width={500} height={500} style={canvasStyle} />
        {last && last.guess && (
            <div style={{...card, padding: '14px 16px', textAlign: 'center', fontSize: 24, fontWeight: 600}}>
                You guessed: <strong>{last.guess}</strong>
            </div>
        )}
        <div style={{textAlign: 'center', opacity: 0.55, fontWeight: 600}}>Waiting for the others…</div>
    </div>;
};
